import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useToast } from './use-toast';
import { DinoDuo, CreateDinoDuoInput } from '@/types/dinoDuos';

export function useDinoDuos() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [dinoDuos, setDinoDuos] = useState<DinoDuo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    fetchDinoDuos();
    setupRealtimeSubscription();
  }, [user]);

  const fetchDinoDuos = async () => {
    if (!user) return;

    try {
      // Get all duos where user is a member
      const { data: memberData, error: memberError } = await supabase
        .from('buddy_read_members')
        .select('buddy_read_id')
        .eq('user_id', user.id);

      if (memberError) throw memberError;

      const duoIds = memberData?.map(m => m.buddy_read_id) || [];

      if (duoIds.length === 0) {
        setDinoDuos([]);
        setLoading(false);
        return;
      }

      // Fetch duo details
      const { data: duosData, error: duosError } = await supabase
        .from('buddy_reads')
        .select(`
          *,
          book:books(id, title, authors, cover_url, page_count)
        `)
        .in('id', duoIds);

      if (duosError) throw duosError;

      // Fetch members for each duo
      const { data: allMembersData, error: allMembersError } = await supabase
        .from('buddy_read_members')
        .select(`*`)
        .in('buddy_read_id', duoIds)
        .eq('status', 'accepted');

      if (allMembersError) throw allMembersError;

      // Fetch member profiles
      const memberUserIds = Array.from(
        new Set((allMembersData || []).map((m: any) => m.user_id).filter(Boolean))
      );

      let profileMap: Record<string, any> = {};
      if (memberUserIds.length > 0) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, username, name, image')
          .in('id', memberUserIds);

        if (profilesError) throw profilesError;

        profileMap = (profilesData || []).reduce((acc: Record<string, any>, profile: any) => {
          acc[profile.id] = profile;
          return acc;
        }, {});
      }

      // Group members by duo
      const membersByDuo = (allMembersData || []).reduce((acc: any, member: any) => {
        if (!acc[member.buddy_read_id]) acc[member.buddy_read_id] = [];
        acc[member.buddy_read_id].push({
          ...member,
          user: profileMap[member.user_id] || null,
        });
        return acc;
      }, {} as Record<string, any[]>);

      setDinoDuos(
        (duosData || []).map((duo: any) => ({
          ...duo,
          book: Array.isArray(duo.book) ? duo.book[0] : duo.book,
          members: membersByDuo?.[duo.id] || [],
        }))
      );
    } catch (error: any) {
      console.error('Error fetching dino duos:', error);
      toast({
        title: "Error loading duos",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const setupRealtimeSubscription = () => {
    if (!user) return;

    const channel = supabase
      .channel('dino-duos-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'buddy_reads'
        },
        () => {
          fetchDinoDuos();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'buddy_read_members'
        },
        (payload) => {
          if (payload.eventType === 'INSERT' && payload.new.user_id === user.id) {
            toast({
              title: "New Duo invitation!",
              description: "You've been invited to join a reading group"
            });
          }
          if (payload.eventType === 'UPDATE' && payload.new.user_id !== user.id) {
            toast({
              title: "Progress update",
              description: "A member updated their reading progress"
            });
          }
          fetchDinoDuos();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const createDinoDuo = async (input: CreateDinoDuoInput) => {
    if (!user) return;

    try {
      // Use the secure RPC function to create the duo
      const { data: duoData, error: duoError } = await supabase.rpc('create_dino_duo', {
        p_book_id: input.book_id,
        p_title: input.title || null,
        p_start_date: input.start_date || null,
        p_target_end_date: input.target_end_date || null,
        p_member_ids: input.member_ids
      });

      if (duoError) throw duoError;

      toast({
        title: "Dino Duo created! 🦕",
        description: `Your reading group has been created${input.member_ids.length > 0 ? ' and invitations sent' : ''}`
      });

      fetchDinoDuos();
    } catch (error: any) {
      console.error('Error creating dino duo:', error);
      toast({
        title: "Error creating duo",
        description: error.message,
        variant: "destructive"
      });
      throw error;
    }
  };

  const updateProgress = async (duoId: string, currentPage: number) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({
          current_page: currentPage,
          last_updated: new Date().toISOString()
        })
        .eq('buddy_read_id', duoId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Progress updated! 📖",
        description: `You're on page ${currentPage}`
      });

      fetchDinoDuos();
    } catch (error: any) {
      console.error('Error updating progress:', error);
      toast({
        title: "Error updating progress",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  const acceptInvite = async (duoId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('buddy_read_members')
        .update({ status: 'accepted', joined_at: new Date().toISOString() })
        .eq('buddy_read_id', duoId)
        .eq('user_id', user.id);

      if (error) throw error;

      toast({
        title: "Welcome to the Duo! 🦕",
        description: "You've joined the reading group"
      });

      fetchDinoDuos();
    } catch (error: any) {
      console.error('Error accepting invite:', error);
      toast({
        title: "Error accepting invite",
        description: error.message,
        variant: "destructive"
      });
    }
  };

  return {
    dinoDuos,
    loading,
    createDinoDuo,
    updateProgress,
    acceptInvite,
    refetch: fetchDinoDuos
  };
}
